import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useListData } from "@react-stately/data";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Header,
  type Key,
  Label,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Text,
  useDragAndDrop,
} from "react-aria-components";
import { FaEllipsisVertical, FaGear } from "react-icons/fa6";
import { GrDrag } from "react-icons/gr";
import { toTitleCase } from "../../../utils/formatText";
import { css, cva } from "../../../../styled-system/css";
import { Center, Grid, HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { gridItem, vstack } from "../../../../styled-system/patterns";
import { Checkbox } from "../../fields/base/Checkbox";
import { Select } from "../../fields/base/Select";
import { SearchInput } from "../../fields/base/TextField";
import { Button } from "../basic/Button";
import { Modal } from "../basic/Modal";
import type { RowData } from "@tanstack/react-table";
import { useLocalStorage } from "@reactuses/core";
import { ButtonGroup } from "../basic/ButtonGroup";
import { type AppTable } from "../../table/tableFeatures";

const METRIC_FILTER_OPTION_VALUES = [
  { value: "all", labelKey: "All Metrics" },
  { value: "merchandising", labelKey: "Merchandising Metrics" },
  { value: "advertising", labelKey: "Advertising Metrics" },
  { value: "calculated", labelKey: "Calculated Metrics" },
  { value: "configuration", labelKey: "Configuration Metrics" },
] as const;

type ColumnOption = {
  id?: string;
  checked?: boolean;
  grouping?: string;
  label?: string;
  toolTip?: string;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  prev_accessor?: (row: any) => unknown;
};

interface Props {
  groupMetrics?: boolean;
  selectorType: "table" | "overview";
  entityType?: string;
  columnOptions: ColumnOption[];
  onMetricChange: (val: Record<string, boolean>) => void;
  onResetMetrics?: () => void;
  setSavedColumnOrder?: (val: Record<string, number>) => void;
}

const ENTITY_LABEL_KEY: Record<
  string,
  | "Media Plan"
  | "Pricing Plan"
  | "Category"
  | "Product"
  | "Organization"
  | "Target"
  | "Product Advertisement"
  | "Content"
> = {
  mediaPlan: "Media Plan",
  pricingPlan: "Pricing Plan",
  category: "Category",
  product: "Product",
  organization: "Organization",
  target: "Target",
  productAd: "Product Advertisement",
  content: "Content",
};

const GROUPING_LABEL_KEY: Record<string, "Merchandising" | "Advertising" | "Calculated" | "Configuration"> = {
  merchandising: "Merchandising",
  advertising: "Advertising",
  calculated: "Calculated",
  configuration: "Configuration",
};

export const LegacyMetricSelector = ({
  selectorType,
  entityType = "product",
  columnOptions,
  onMetricChange,
  onResetMetrics = () => {},
  groupMetrics = true,
  setSavedColumnOrder = () => {},
}: Props) => {
  const { t } = useTranslation(["common"]);
  const columnSelectorModal = useModal(ColumnSelectorModal);
  const onSubmit = (selectedColumns: Record<string, boolean>, columnOrder: string[]) => {
    // Below is to handle keeping metrics that don't have a column order, such as the (Previous) metrics in our charts
    const selectedMetricIds = Object.entries(selectedColumns)

      // oxlint-disable-next-line no-unused-vars
      .filter(([_, isSelected]) => isSelected)
      .map(([metricId]) => metricId);

    const completeColumnOrder = [...columnOrder];
    selectedMetricIds.forEach((id) => {
      if (!completeColumnOrder.includes(id)) {
        completeColumnOrder.push(id);
      }
    });

    onMetricChange(selectedColumns);
    setSavedColumnOrder(
      completeColumnOrder.reduce(
        (acc, column, index) => {
          acc[column] = index;
          return acc;
        },
        {} as Record<string, number>,
      ),
    );
  };

  const entityLabelKey = ENTITY_LABEL_KEY[entityType];
  const entityLabel = entityLabelKey ? t(($) => $.common.metricSelector.entityLabels[entityLabelKey]) : "";
  const selectorTypeLabel =
    selectorType === "overview" ?
      t(($) => $.common.metricSelector["Overview"])
    : t(($) => $.common.metricSelector["Table"]);
  const modalTitle = t(($) => $.common.metricSelector["Select {entity} {selectorType} Metrics"], {
    entity: entityLabel,
    selectorType: selectorTypeLabel,
  });

  return (
    <Button
      variant={"contentOnly"}
      onPress={() =>
        void columnSelectorModal.show({
          title: modalTitle,
          initialState: columnOptions,
          onReset: onResetMetrics,
          selectorType,
          groupMetrics,
          canReorder: true,
          canSelectPrevMetrics: selectorType === "overview",
          onSubmit,
        })
      }
      fontSize="large"
    >
      {selectorType === "overview" ?
        <FaEllipsisVertical />
      : <FaGear />}
    </Button>
  );
};

export const RT8ColumnSelector = <TData extends RowData>({ table }: { table: AppTable<TData> }) => {
  const columnSelectorModal = useModal(ColumnSelectorModal);
  const { t } = useTranslation(["metrics", "common"]);
  const columnOptions: ColumnOption[] = table
    .getAllColumns()
    .filter((column) => column.getCanHide())
    .map((column) => {
      const printableHeader =
        typeof column.columnDef.header === "string" ? column.columnDef.header : toTitleCase(column.id);
      const labelMeta = column.columnDef.meta?.columnSelectorOptions?.label;
      const label =
        labelMeta == null ? printableHeader
        : typeof labelMeta === "string" ? labelMeta
        : t(labelMeta);
      const description = column.columnDef.meta?.columnSelectorOptions?.description;
      const toolTip =
        description == null ? ""
        : typeof description === "string" ? description
        : t(description);
      return {
        id: column.id,
        checked: column.getIsVisible(),
        label,
        toolTip,
        grouping: column.columnDef.meta?.columnSelectorOptions?.grouping,
      };
    })
    .sort((a, b) => {
      const { columnOrder } = table.state;
      const aOrderIndex = columnOrder.indexOf(a.id);
      const bOrderIndex = columnOrder.indexOf(b.id);

      return (aOrderIndex === -1 ? Infinity : aOrderIndex) - (bOrderIndex === -1 ? Infinity : bOrderIndex);
    });

  const onReset = () => {
    table.resetColumnVisibility();
    table.resetColumnOrder();
  };

  const onSubmit = (selectedColumns: Record<string, boolean>, columnOrder: string[]) => {
    table.setColumnVisibility((current) => ({ ...current, ...selectedColumns }));
    table.setColumnOrder(columnOrder);
  };

  return (
    <Button
      variant={"contentOnly"}
      onPress={() =>
        void columnSelectorModal.show({
          title: table.options.meta?.columnSelectOptions?.modalTitle,
          initialState: columnOptions,
          onReset,
          onSubmit,
        })
      }
    >
      <FaGear />
    </Button>
  );
};

interface ModalProps {
  initialState: ColumnOption[];
  title?: string;
  groupMetrics?: boolean;
  canReorder?: boolean;
  canSelectPrevMetrics?: boolean;
  onReset: () => void;
  onSubmit: (selectedColumns: Record<string, boolean>, columnOrder: string[]) => void;
  selectorType: "table" | "overview";
}

const ColumnSelectorModal = NiceModal.create(
  ({
    title,
    initialState,
    onReset,
    onSubmit,
    groupMetrics = true,
    canReorder = true,
    canSelectPrevMetrics = false,
    selectorType,
  }: ModalProps) => {
    const { t } = useTranslation(["common", "tags"]);
    const modal = useModal();
    const metricSelectFilterOptions = METRIC_FILTER_OPTION_VALUES.map((option) => ({
      value: option.value,
      label: t(($) => $.common.metricSelector.filterOptions[option.labelKey]),
    }));
    const [metricSelectFilter, setMetricSelectFilter] = useState<Key | null>(METRIC_FILTER_OPTION_VALUES[0].value);
    const resolvedTitle = title ?? t(($) => $.common.metricSelector["Select columns"]);
    const [showResetConfirmation, setShowResetConfirmation] = useState(false);
    const [numberFormat, setNumberFormat] = useLocalStorage("numberFormat", "compact") as [
      string,
      React.Dispatch<React.SetStateAction<string | null>>,
    ];

    const selectColumnList = useListData({
      initialItems: initialState,
      initialSelectedKeys: new Set(initialState.filter((option) => option.checked).map((option) => option.id ?? "")),
      filter(item, filterText) {
        const matchesSearch = `${item.label} ${item.toolTip}`.toLowerCase().includes(filterText.toLowerCase());
        const matchesCategoryFilter = metricSelectFilter === "all" || item.grouping === metricSelectFilter;
        const shouldHidePrev = canSelectPrevMetrics && item.id?.endsWith("_prev");
        return matchesSearch && matchesCategoryFilter && !shouldHidePrev;
      },
    });

    const metricsByGrouping = Object.groupBy(selectColumnList.items, ({ grouping }) =>
      groupMetrics ? (grouping ?? "unknown") : "unknown",
    );

    const columnOrderList = useListData({
      initialItems: initialState,
      filter: (item) => {
        const hideUnselected =
          selectColumnList.selectedKeys === "all" || selectColumnList.selectedKeys.has(item.id ?? "");
        const shouldHidePrev = canSelectPrevMetrics && item.id?.endsWith("_prev");
        return hideUnselected && !shouldHidePrev;
      },
    });

    const { dragAndDropHooks } = useDragAndDrop({
      isDisabled: !canReorder,
      getItems: (keys) => [...keys].map((key) => ({ "text/plain": columnOrderList.getItem(key)?.label ?? "" })),
      onReorder(e) {
        if (e.target.dropPosition === "before") {
          columnOrderList.moveBefore(e.target.key, e.keys);
        } else if (e.target.dropPosition === "after") {
          columnOrderList.moveAfter(e.target.key, e.keys);
        }
      },
      renderDropIndicator: (target) => {
        // oxlint-disable-next-line react/jsx-no-useless-fragment
        if (target.type === "root") return <></>;

        return (
          <div
            className={css({
              alignSelf: "stretch",
              borderBottom: "3px solid {colors.trellisPalette.2}",
              height: "{fontSizes.large}",
            })}
          />
        );
      },
    });

    return (
      <Modal
        isOpen={modal.visible}
        isDismissable
        onOpenChange={(isOpen) => (isOpen ? void 0 : modal.remove())}
        title={resolvedTitle}
        css={{ minWidth: "60vw", height: "85vh" }}
      >
        <VStack flex="auto" gap="small" height={"100%"} paddingX={"large"} overflow="hidden">
          {showResetConfirmation ?
            <>
              <p className={css({ width: "75%", textAlign: "center" })}>
                {canReorder ?
                  t(
                    ($) =>
                      $.common.metricSelector[
                        "Are you sure you want to reset current selection and ordering preferences to the default configuration?"
                      ],
                  )
                : t(
                    ($) =>
                      $.common.metricSelector[
                        "Are you sure you want to reset current selection preferences to the default configuration?"
                      ],
                  )
                }
              </p>
              <Center gap={"large"}>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setShowResetConfirmation(false);
                  }}
                >
                  {t(($) => $.common.actions["Cancel"])}
                </Button>
                <Button
                  onPress={() => {
                    onReset();
                    modal.remove();
                    setShowResetConfirmation(false);
                  }}
                >
                  {t(($) => $.tags.customTag["Confirm"])}
                </Button>
              </Center>
            </>
          : <>
              <Wrap
                gap={{ base: "small", md: "medium", lg: "large" }}
                borderBottom={"1px solid {colors.lightgrey}"}
                width={"100%"}
                paddingBottom={"medium"}
              >
                <SearchInput
                  aria-label={t(($) => $.common.actions["Search"])}
                  css={{ flex: "1" }}
                  value={selectColumnList.filterText}
                  onChange={(val) => selectColumnList.setFilterText(val)}
                  placeholder={t(($) => $.common.searchBar["Search..."])}
                />
                {groupMetrics && (
                  <Select
                    aria-label={t(($) => $.common.metricSelector["Filter by category"])}
                    css={{ flex: "1" }}
                    items={metricSelectFilterOptions}
                    selectedKey={metricSelectFilter}
                    onSelectionChange={setMetricSelectFilter}
                  />
                )}

                <Button
                  onPress={() => {
                    setShowResetConfirmation(true);
                  }}
                >
                  {t(($) => $.common.metricSelector["Reset"])}
                </Button>
              </Wrap>
              <Grid flex={"1"} width={"100%"} marginTop={"-small"} overflow={"hidden"} columns={3}>
                <ListBox
                  className={gridItem({
                    colSpan: 2,
                    borderRight: "1px solid {colors.lightgrey}",
                    overflowY: "auto",
                  })}
                  selectionMode="multiple"
                  selectedKeys={selectColumnList.selectedKeys}
                  onSelectionChange={(keys) => selectColumnList.setSelectedKeys(keys)}
                >
                  {Object.entries(metricsByGrouping).map(([grouping, columnOptions = []]) => {
                    const groupingLabelKey = GROUPING_LABEL_KEY[grouping];
                    return (
                      <ListBoxSection
                        key={grouping}
                        className={vstack({
                          alignItems: "start",
                          borderBottom: { base: "1px solid {colors.lightgrey}", _last: "none" },
                          padding: "medium",
                        })}
                      >
                        {grouping.toLowerCase() !== "unknown" && (
                          <Header
                            className={css({
                              fontSize: "large",
                              fontWeight: "bold",
                            })}
                          >
                            {groupingLabelKey ?
                              t(($) => $.common.metricSelector[groupingLabelKey])
                            : toTitleCase(grouping)}
                          </Header>
                        )}
                        {columnOptions

                          .sort((a, b) => a.label?.localeCompare(b.label ?? "") ?? 0)
                          .map((metric) => {
                            const { id, label, toolTip } = metric;

                            return (
                              <ListBoxItem
                                key={id}
                                id={id}
                                textValue={label}
                                className={css({
                                  cursor: "pointer",
                                  display: "flex",
                                  gap: "small",
                                  alignItems: "start",
                                })}
                              >
                                {({ isSelected }) => (
                                  <>
                                    <Checkbox
                                      size="small"
                                      value={isSelected}
                                      className={css({ width: "min-content" })}
                                    />
                                    <VStack gap="xsmall" alignItems={"start"}>
                                      <Text
                                        slot={"label"}
                                        className={css({
                                          fontSize: "large",
                                          fontWeight: "500",
                                          lineHeight: "1",
                                        })}
                                      >
                                        {label}
                                      </Text>
                                      <Text
                                        slot={"description"}
                                        className={css({
                                          color: "text.secondary",
                                          fontSize: "small",
                                        })}
                                      >
                                        {toolTip}
                                      </Text>
                                    </VStack>
                                  </>
                                )}
                              </ListBoxItem>
                            );
                          })}
                      </ListBoxSection>
                    );
                  })}
                </ListBox>
                <div className={css({ overflowY: "auto" })}>
                  {selectorType === "overview" ?
                    <HStack
                      className={css({ justifyContent: "space-between", paddingX: "small", paddingTop: "medium" })}
                    >
                      <Label
                        className={css({
                          fontSize: "large",
                          fontWeight: "bold",
                        })}
                      >
                        {t(($) => $.common.metricSelector["Number format:"])}
                      </Label>
                      <ButtonGroup
                        buttons={[
                          {
                            value: "compact",
                            label: t(($) => $.common.metricSelector["Compact"]),
                          },
                          {
                            value: "precise",
                            label: t(($) => $.common.metricSelector["Precise"]),
                          },
                        ]}
                        onSelect={(value) => {
                          setNumberFormat(value);
                        }}
                        value={numberFormat}
                      />
                    </HStack>
                  : null}
                  <ListBox
                    className={gridItem({
                      display: "flex",
                      gap: "small",
                      flexDirection: "column",
                      padding: "small",
                      overflowY: "auto",
                      minHeight: 0,
                    })}
                    items={columnOrderList.items}
                    dragAndDropHooks={dragAndDropHooks}
                  >
                    <Header
                      className={css({
                        fontSize: "large",
                        fontWeight: "bold",
                      })}
                    >
                      {t(($) => $.common.metricSelector["Selected Metrics"])}
                    </Header>
                    {columnOrderList.items.map((metric) => {
                      return (
                        <ListBoxItem
                          key={metric.id}
                          textValue={metric.label}
                          id={metric.id}
                          className={css({
                            cursor: canReorder ? "grab" : "default",
                            display: "flex",

                            gap: "small",
                            alignItems: "center",
                            fontSize: "large",
                            fontWeight: 500,
                            lineHeight: "1",
                            "&[data-dragging]": {
                              opacity: 0.5,
                            },
                          })}
                        >
                          <Checkbox
                            value={true}
                            size="small"
                            className={css({ cursor: "pointer", display: "inline-flex", width: "min-content" })}
                            onChange={() => {
                              let nextSelected = new Set(selectColumnList.selectedKeys);
                              if (selectColumnList.selectedKeys === "all") {
                                nextSelected = new Set(initialState.map((metric) => metric.id ?? ""));
                              }
                              nextSelected.delete(metric.id ?? "");
                              selectColumnList.setSelectedKeys(nextSelected);
                            }}
                          />
                          {metric.label}
                          {!!metric.prev_accessor && (
                            <Button
                              variant={"contentOnly"}
                              className={cva({
                                base: {
                                  cursor: "pointer",
                                  border: `1px solid {colors.trellisPalette.3}`,
                                  borderRadius: "3px",
                                  height: "17px",
                                  padding: "xsmall",
                                  color: "trellisPalette.3",
                                  fontSize: "small",
                                  fontWeight: "500",
                                  backgroundColor: "white",
                                  fontStyle: "italic",
                                },
                                variants: {
                                  selected: {
                                    true: {
                                      color: "#fff",
                                      backgroundColor: "trellisPalette.3",
                                    },
                                  },
                                },
                              })({
                                selected:
                                  selectColumnList.selectedKeys === "all" ||
                                  selectColumnList.selectedKeys.has(`${metric.id}_prev`),
                              })}
                              onPress={() => {
                                let nextSelected = new Set(selectColumnList.selectedKeys);
                                if (selectColumnList.selectedKeys === "all") {
                                  nextSelected = new Set(initialState.map((metric) => metric.id ?? ""));
                                }
                                const id = `${metric.id}_prev`;
                                const prevMetricSelected = nextSelected.has(id);

                                if (prevMetricSelected) {
                                  nextSelected.delete(id);
                                } else {
                                  nextSelected.add(id);
                                }

                                selectColumnList.setSelectedKeys(nextSelected);
                              }}
                            >
                              {t(($) => $.common.metricSelector["vs"])}
                            </Button>
                          )}
                          {canReorder && <GrDrag className={css({ marginLeft: "auto" })} />}
                        </ListBoxItem>
                      );
                    })}
                  </ListBox>
                </div>
              </Grid>
              <Center gap="large" marginTop="auto">
                <Button
                  variant="secondary"
                  onPress={() => {
                    modal.remove();
                  }}
                >
                  {t(($) => $.common.actions["Cancel"])}
                </Button>
                <Button
                  onPress={() => {
                    const nextSelected = Object.fromEntries(
                      initialState.map(({ id }) => {
                        const idForCurrent = id?.endsWith("_prev") ? id.replace("_prev", "") : id;
                        const isPreviousWithoutCurrent =
                          id?.endsWith("_prev") &&
                          selectColumnList.selectedKeys !== "all" &&
                          !selectColumnList.selectedKeys.has(idForCurrent ?? "");

                        return [
                          id,
                          (selectColumnList.selectedKeys === "all" || selectColumnList.selectedKeys.has(id ?? "")) &&
                            !isPreviousWithoutCurrent,
                        ];
                      }),
                    );

                    const nextOrder = columnOrderList.items.map(({ id }) => id ?? "");
                    onSubmit(nextSelected, nextOrder);

                    modal.remove();
                  }}
                >
                  {t(($) => $.common.actions["Apply"])}
                </Button>
              </Center>
            </>
          }
        </VStack>
      </Modal>
    );
  },
);
